import axios from 'axios';
import { toast } from 'react-toastify';
import { Product, Category, User, Order, Address, HomepageContent, NavigationMenu, NavigationMenuItem, AnnouncementBar, ShippingAddress, BillingAddress } from '../types';

// API base URL
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json'
  },
  withCredentials: true
});

// Add token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle response errors with token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 403 && error.response?.data?.code === 'OBSERVER_READONLY') {
      const message = error.response.data.message || 'Bu işlem için yetkiniz bulunmamaktadır.';
      toast.error(message, { position: 'top-right', autoClose: 4000 });
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      // Check if this is a login or register request - don't refresh for those
      const url = originalRequest?.url || '';
      const authRoutes = ['/users/login', '/users/register', '/users/verify-email', '/users/refresh-token'];
      if (authRoutes.some(route => url.includes(route))) {
        return Promise.reject(error);
      }

      originalRequest._retry = true;

      try {
        const token = localStorage.getItem('token');
        if (token) {
          const response = await axios.post(
            `${API_URL}/users/refresh-token`,
            {},
            { headers: { Authorization: `Bearer ${token}` } }
          );
          const newToken = response.data.token;
          localStorage.setItem('token', newToken);
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return api(originalRequest);
        }
      } catch {
        // Refresh failed, clear auth below
      }

      // Only clear auth for protected routes
      const protectedRoutes = ['/orders', '/admin', '/users/profile', '/users/addresses'];
      if (protectedRoutes.some(route => url.includes(route))) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.dispatchEvent(new CustomEvent('auth:logout'));
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: async (email: string, password: string) => {
    const response = await api.post('/users/login', { email, password });
    const { token, user } = response.data;

    return {
      success: true,
      user: {
        _id: user._id,
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        role: user.role,
        adminRole: user.adminRole
      },
      token,
      message: response.data.message
    };
  },

  register: async (userData: {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
  }) => {
    const response = await api.post('/users/register', userData);
    const { token, user, requiresVerification, message } = response.data;

    return {
      success: true,
      user: {
        _id: user._id,
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        role: user.role
      },
      token,
      message,
      requiresVerification: requiresVerification || false
    };
  },
};

// Users API
export const usersAPI = {
  getProfile: async (options?: { signal?: AbortSignal }) => {
    const response = await api.get('/users/profile', { signal: options?.signal });
    return response.data;
  },

  updateProfile: async (data: { firstName: string; lastName: string; phone: string }) => {
    const response = await api.put('/users/profile', data);
    return response.data;
  },

  updatePassword: async (data: { currentPassword: string; newPassword: string }) => {
    const response = await api.post('/users/update-password', data);
    return response.data;
  },

  getAddresses: async () => {
    const response = await api.get('/users/addresses');
    return response.data;
  },

  addAddress: async (address: Partial<Address>) => {
    const response = await api.post('/users/addresses', address);
    return response.data;
  },

  updateAddress: async (addressId: string, address: Partial<Address>) => {
    const response = await api.put(`/users/addresses/${addressId}`, address);
    return response.data;
  },

  deleteAddress: async (addressId: string) => {
    const response = await api.delete(`/users/addresses/${addressId}`);
    return response.data;
  }
};

// Products API
export const productsAPI = {
  getProducts: async (params?: {
    page?: number;
    limit?: number;
    category?: string;
    minPrice?: number;
    maxPrice?: number;
    search?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    featured?: boolean;
  }) => {
    try {
      // Add timestamp to prevent caching
      const response = await api.get('/products', {
        params: { ...params, _t: Date.now() }
      });
      return {
        success: true,
        products: response.data.products,
        totalPages: response.data.totalPages,
        currentPage: response.data.currentPage,
        totalItems: response.data.totalProducts,
        totalProducts: response.data.totalProducts
      };
    } catch (error) {
      console.error('Error fetching products:', error);
      throw error;
    }
  },

  getProduct: async (slug: string): Promise<Product> => {
    // Add timestamp to prevent caching
    const response = await api.get(`/products/${slug}`, {
      params: { _t: Date.now() }
    });
    return response.data;
  },

  getRelatedProducts: async (productId: string): Promise<Product[]> => {
    const response = await api.get(`/products/${productId}/related`);
    return response.data;
  }
};

// Categories API
export const categoriesAPI = {
  getCategories: async (): Promise<Category[]> => {
    const response = await api.get('/categories');
    return response.data;
  },

  getCategory: async (slug: string) => {
    const response = await api.get(`/categories/${slug}`);
    return {
      success: true,
      category: response.data.category,
      products: response.data.products
    };
  }
};

// Orders API
export const ordersAPI = {
  createOrder: async (orderData: {
    items: Array<{
      productId: string;
      quantity: number;
    }>;
    shippingAddress: ShippingAddress;
    billingAddress: BillingAddress;
    paymentMethod: string;
  }): Promise<Order> => {
    const response = await api.post('/orders', orderData);

    // Clear cart after successful order
    localStorage.removeItem('cart');

    return response.data;
  },

  getOrders: async (): Promise<Order[]> => {
    const response = await api.get('/orders');
    return response.data;
  },

  getOrder: async (orderId: string): Promise<Order> => {
    const response = await api.get(`/orders/${orderId}`);
    return response.data;
  },

  createGuestOrder: async (orderData: {
    items: Array<{
      productId: string;
      quantity: number;
    }>;
    guestInfo: {
      email: string;
      firstName: string;
      lastName: string;
      phone: string;
    };
    shippingAddress: ShippingAddress;
    billingAddress: BillingAddress;
    paymentMethod: string;
    createAccount?: {
      password: string;
    };
  }): Promise<{
    success: boolean;
    message: string;
    message_en?: string;
    orderId: string;
    orderNumber: string;
    totalAmount: number;
    accountCreated: boolean;
    confirmationToken?: string;
  }> => {
    const response = await api.post('/orders/guest', orderData);
    
    // Clear cart after successful order
    localStorage.removeItem('cart');
    
    return response.data;
  },

  trackGuestOrder: async (email: string, orderNumber: string): Promise<Order> => {
    const response = await api.get('/orders/track', {
      params: { email, orderNumber }
    });
    return response.data;
  },

  cancelOrder: async (orderId: string, reason?: string): Promise<{ success: boolean; message: string }> => {
    const response = await api.post(`/orders/${orderId}/cancel`, { reason });
    return response.data;
  },

  cancelGuestOrder: async (orderNumber: string, email: string, reason?: string): Promise<{ success: boolean; message: string }> => {
    const response = await api.post(`/orders/guest/${orderNumber}/cancel`, {
      email,
      reason
    });
    return response.data;
  },

  checkFirstOrderDiscount: async (): Promise<{
    success: boolean;
    eligible: boolean;
    percentage: number;
    message: { tr: string; en: string } | null;
  }> => {
    const token = localStorage.getItem('token');
    if (!token) {
      return { success: true, eligible: false, percentage: 0, message: null };
    }

    try {
      const response = await api.get('/orders/first-order-discount');
      return response.data;
    } catch (error) {
      return { success: false, eligible: false, percentage: 0, message: null };
    }
  }
};

// Admin API
export const adminAPI = {
  getStats: async () => {
    const response = await api.get('/admin/stats');
    return response.data;
  },

  getProducts: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
    category?: string;
    status?: string;
    stockStatus?: string;
    sortBy?: string;
    lowStockThreshold?: number;
  }) => {
    const response = await api.get('/admin/products', { params });
    return response.data;
  },

  getProduct: async (productId: string) => {
    const response = await api.get(`/admin/products/${productId}`);
    return response.data;
  },

  batchProducts: async (data: { ids: string[]; action: 'status' | 'delete'; value?: string }) => {
    const response = await api.patch('/admin/products/batch', data);
    return response.data;
  },

  createProduct: async (productData: Partial<Product>) => {
    const response = await api.post('/admin/products', productData);
    return response.data;
  },

  updateProduct: async (productId: string, productData: Partial<Product>) => {
    const response = await api.put(`/admin/products/${productId}`, productData);
    return response.data;
  },

  deleteProduct: async (productId: string) => {
    const response = await api.delete(`/admin/products/${productId}`);
    return response.data;
  },

  reorderProducts: async (products: Array<{ id: string; displayOrder: number }>) => {
    const response = await api.patch('/admin/products/reorder', { products });
    return response.data;
  },

  getOrders: async (params?: {
    page?: number;
    limit?: number;
    status?: string;
    paymentStatus?: string;
    search?: string;
    sort?: string;
    dateFrom?: string;
    dateTo?: string;
  }) => {
    const response = await api.get('/admin/orders', { params });
    return response.data;
  },

  getOrderStats: async () => {
    const response = await api.get('/admin/orders/stats');
    return response.data;
  },

  getOrder: async (orderId: string) => {
    const response = await api.get(`/admin/orders/${orderId}`);
    return response.data;
  },

  updateOrderDetails: async (orderId: string, data: {
    status?: string;
    trackingNumber?: string;
    carrier?: string;
    paymentStatus?: string;
  }) => {
    const response = await api.put(`/admin/orders/${orderId}/status`, data);
    return response.data;
  },

  // Backward-compatible alias
  updateOrder: async (orderId: string, data: {
    status?: string;
    trackingNumber?: string;
    carrier?: string;
    paymentStatus?: string;
  }) => {
    const response = await api.put(`/admin/orders/${orderId}/status`, data);
    return response.data;
  },

  batchOrders: async (data: { ids: string[]; action: string; value: string }) => {
    const response = await api.patch('/admin/orders/batch', data);
    return response.data;
  },

  exportOrders: async (params?: {
    status?: string;
    paymentStatus?: string;
    search?: string;
    dateFrom?: string;
    dateTo?: string;
  }) => {
    const response = await api.get('/admin/orders/export', {
      params,
      responseType: 'blob'
    });
    return response.data;
  },

  completeOrder: async (orderId: string, formData: FormData) => {
    const response = await api.post(`/admin/orders/${orderId}/complete`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  getLowStockProducts: async (threshold: number = 10) => {
    const response = await api.get('/admin/products/low-stock', {
      params: { threshold }
    });
    return response.data;
  },

  adjustProductStock: async (productId: string, adjustment: number, reason?: string) => {
    const response = await api.post(`/admin/products/${productId}/stock`, {
      adjustment,
      reason
    });
    return response.data;
  },

  cancelOrder: async (orderId: string, reason?: string) => {
    const response = await api.post(`/admin/orders/${orderId}/cancel`, { reason });
    return response.data;
  },

  // Categories
  getCategories: async () => {
    const response = await api.get('/admin/categories');
    return response.data;
  },

  createCategory: async (data: Partial<Category>) => {
    const response = await api.post('/admin/categories', data);
    return response.data;
  },

  updateCategory: async (id: string, data: Partial<Category>) => {
    const response = await api.put(`/admin/categories/${id}`, data);
    return response.data;
  },

  deleteCategory: async (id: string) => {
    const response = await api.delete(`/admin/categories/${id}`);
    return response.data;
  }
};

// Homepage Content API
export const homepageContentAPI = {
  getContent: async (): Promise<HomepageContent> => {
    const response = await api.get('/homepage-content');
    return response.data;
  },

  getContentAdmin: async (): Promise<HomepageContent> => {
    const response = await api.get('/homepage-content/admin');
    return response.data;
  },

  updateContent: async (content: Partial<HomepageContent>): Promise<HomepageContent> => {
    const response = await api.put('/homepage-content', content);
    return response.data;
  },

  updateSection: async (section: string, data: Partial<HomepageContent>): Promise<HomepageContent> => {
    const response = await api.put(`/homepage-content/${section}`, data);
    return response.data;
  },

  uploadVideo: async (file: File): Promise<{ success: boolean; file: { url: string; publicId: string } }> => {
    const formData = new FormData();
    formData.append('video', file);
    const response = await api.post('/homepage-content/upload/video', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  uploadImage: async (file: File): Promise<{ success: boolean; file: { url: string; publicId: string } }> => {
    const formData = new FormData();
    formData.append('image', file);
    const response = await api.post('/homepage-content/upload/image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  initialize: async (): Promise<HomepageContent> => {
    const response = await api.post('/homepage-content/initialize');
    return response.data.content;
  }
};

// Navigation Menu API
export const navigationMenuAPI = {
  getMenu: async (): Promise<NavigationMenu> => {
    const response = await api.get('/navigation-menu');
    return response.data;
  },

  getMenuAdmin: async (): Promise<NavigationMenu> => {
    const response = await api.get('/navigation-menu/admin');
    return response.data;
  },

  updateMenu: async (mainMenu: NavigationMenuItem[]): Promise<NavigationMenu> => {
    const response = await api.put('/navigation-menu', { mainMenu });
    return response.data.navigation;
  },

  addItem: async (item: Omit<NavigationMenuItem, '_id'>): Promise<NavigationMenu> => {
    const response = await api.post('/navigation-menu/item', item);
    return response.data.navigation;
  },

  updateItem: async (itemId: string, updates: Partial<NavigationMenuItem>): Promise<NavigationMenu> => {
    const response = await api.put(`/navigation-menu/item/${itemId}`, updates);
    return response.data.navigation;
  },

  deleteItem: async (itemId: string): Promise<NavigationMenu> => {
    const response = await api.delete(`/navigation-menu/item/${itemId}`);
    return response.data.navigation;
  },

  reorderItems: async (itemIds: string[]): Promise<NavigationMenu> => {
    const response = await api.put('/navigation-menu/reorder', { itemIds });
    return response.data.navigation;
  },

  initialize: async (): Promise<NavigationMenu> => {
    const response = await api.post('/navigation-menu/initialize');
    return response.data.navigation;
  }
};

// Announcement Bar API
export const announcementBarAPI = {
  get: async (): Promise<AnnouncementBar> => {
    const response = await api.get('/announcement-bar');
    return response.data;
  },

  getAdmin: async (): Promise<AnnouncementBar> => {
    const response = await api.get('/announcement-bar/admin');
    return response.data;
  },

  update: async (data: Partial<AnnouncementBar>): Promise<AnnouncementBar> => {
    const response = await api.put('/announcement-bar', data);
    return response.data.announcement;
  }
};

// Accessory type
export interface Accessory {
  _id: string;
  name: string;
  name_en?: string;
  description?: string;
  description_en?: string;
  price: number;
  images: { url: string; alt?: string }[];
  sku: string;
  stock: number;
  isActive: boolean;
  displayOrder: number;
  createdAt?: string;
  updatedAt?: string;
}

// Accessories API
export const accessoriesAPI = {
  getAll: async (activeOnly: boolean = false): Promise<Accessory[]> => {
    const response = await api.get(`/accessories${activeOnly ? '?active=true&limit=100' : '?limit=100'}`);
    return response.data.data || response.data;
  },

  getById: async (id: string): Promise<Accessory> => {
    const response = await api.get(`/accessories/${id}`);
    return response.data;
  },

  create: async (data: Omit<Accessory, '_id' | 'createdAt' | 'updatedAt'>): Promise<Accessory> => {
    const response = await api.post('/accessories', data);
    return response.data.accessory;
  },

  update: async (id: string, data: Partial<Accessory>): Promise<Accessory> => {
    const response = await api.put(`/accessories/${id}`, data);
    return response.data.accessory;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/accessories/${id}`);
  },

  reorder: async (items: { id: string; displayOrder: number }[]): Promise<void> => {
    await api.put('/accessories/reorder/bulk', { items });
  }
};

// Shipping Settings type
export interface ShippingSettings {
  _id?: string;
  drawerTitle?: string;
  drawerTitle_en?: string;
  drawerContent?: string;
  drawerContent_en?: string;
  shippingIntroText: string;
  shippingIntroText_en: string;
  shippingInfo: string[];
  shippingInfo_en: string[];
  returnsInfo: string[];
  returnsInfo_en: string[];
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

// Shipping Settings API
export const shippingSettingsAPI = {
  get: async (): Promise<ShippingSettings> => {
    const response = await api.get('/shipping-settings');
    return response.data;
  },

  getAdmin: async (): Promise<ShippingSettings> => {
    const response = await api.get('/shipping-settings/admin');
    return response.data;
  },

  update: async (data: Partial<ShippingSettings>): Promise<ShippingSettings> => {
    const response = await api.put('/shipping-settings', data);
    return response.data.settings;
  },

  initialize: async (): Promise<ShippingSettings> => {
    const response = await api.post('/shipping-settings/initialize');
    return response.data.settings;
  }
};

// Admin User type
export interface AdminUser {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  adminRole: 'full' | 'observer';
  isActive: boolean;
  lastLogin?: string;
  createdAt?: string;
}

// Admin Users API
export const adminUsersAPI = {
  list: async (): Promise<{ users: AdminUser[] }> => {
    const response = await api.get('/admin/users');
    return response.data;
  },

  create: async (data: {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    adminRole: 'full' | 'observer';
  }): Promise<{ message: string; user: AdminUser }> => {
    const response = await api.post('/admin/users', data);
    return response.data;
  },

  update: async (
    id: string,
    data: { adminRole?: 'full' | 'observer'; isActive?: boolean; password?: string }
  ): Promise<{ message: string; user: AdminUser }> => {
    const response = await api.put(`/admin/users/${id}`, data);
    return response.data;
  },

  remove: async (id: string): Promise<{ message: string }> => {
    const response = await api.delete(`/admin/users/${id}`);
    return response.data;
  }
};

export default api;